import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Copy, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";

export interface ContactInfo {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

export interface CompanyInfo {
  companyName: string;
  orgNumber: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  email: string;
  phone: string;
}

export interface BillingInfo {
  address: string;
  postalCode: string;
  city: string;
  country: string;
  reference: string;
  poNumber: string;
  invoiceMethod: "email" | "einvoice" | "letter";
  invoiceEmail: string;
  vatNumber: string;
}

interface Props {
  contact: ContactInfo;
  company: CompanyInfo;
  billing: BillingInfo;
  termsAccepted: boolean;
  onContactChange: (c: ContactInfo) => void;
  onCompanyChange: (c: CompanyInfo) => void;
  onBillingChange: (b: BillingInfo) => void;
  onTermsChange: (v: boolean) => void;
}

const COUNTRIES = [
  { value: "SE", label: "Sverige" },
  { value: "NO", label: "Norge" },
  { value: "DK", label: "Danmark" },
  { value: "FI", label: "Finland" },
];

export function OrderCustomerForm({
  contact,
  company,
  billing,
  termsAccepted,
  onContactChange,
  onCompanyChange,
  onBillingChange,
  onTermsChange,
}: Props) {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  const copyFromCompany = () => {
    onBillingChange({
      ...billing,
      address: company.address,
      postalCode: company.postalCode,
      city: company.city,
      country: company.country,
    });
  };

  return (
    <div className="space-y-10">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">
          {t("order.customerDetails")}
        </h2>
        <p className="text-muted-foreground mt-1">
          {t("order.customerDetailsDesc")}
        </p>
      </div>

      {/* Contact */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">
          {t("order.contactInfo")}
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>{t("order.firstName")} *</Label>
            <Input
              value={contact.firstName}
              onChange={(e) => onContactChange({ ...contact, firstName: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.lastName")} *</Label>
            <Input
              value={contact.lastName}
              onChange={(e) => onContactChange({ ...contact, lastName: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.phone")} *</Label>
            <Input
              value={contact.phone}
              onChange={(e) => onContactChange({ ...contact, phone: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.email")} *</Label>
            <Input
              type="email"
              value={contact.email}
              onChange={(e) => onContactChange({ ...contact, email: e.target.value })}
            />
          </div>
        </div>
      </section>

      {/* Company */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">
          {t("order.companyInfo")}
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>{t("order.companyName")} *</Label>
            <Input
              value={company.companyName}
              onChange={(e) => onCompanyChange({ ...company, companyName: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.orgNumber")} *</Label>
            <Input
              value={company.orgNumber}
              onChange={(e) => onCompanyChange({ ...company, orgNumber: e.target.value })}
              placeholder="0000000000"
              maxLength={10}
            />
            <p className="text-xs text-muted-foreground mt-1">{t("order.orgNumberHint")}</p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("order.address")} *</Label>
            <Input
              value={company.address}
              onChange={(e) => onCompanyChange({ ...company, address: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.postalCode")} *</Label>
            <Input
              value={company.postalCode}
              onChange={(e) => onCompanyChange({ ...company, postalCode: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.city")} *</Label>
            <Input
              value={company.city}
              onChange={(e) => onCompanyChange({ ...company, city: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.country")} *</Label>
            <select
              value={company.country}
              onChange={(e) => onCompanyChange({ ...company, country: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {COUNTRIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.email")}</Label>
            <Input
              type="email"
              value={company.email}
              onChange={(e) => onCompanyChange({ ...company, email: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.phone")}</Label>
            <Input
              value={company.phone}
              onChange={(e) => onCompanyChange({ ...company, phone: e.target.value })}
            />
          </div>
        </div>
      </section>

      {/* Billing */}
      <section className="space-y-4">
        <div className="flex items-center justify-between border-b border-border pb-2">
          <h3 className="text-lg font-semibold text-foreground">
            {t("order.billingInfo")}
          </h3>
          <Button variant="outline" size="sm" onClick={copyFromCompany} className="gap-1.5">
            <Copy className="h-3.5 w-3.5" />
            {t("order.copyFromCompany")}
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("order.billingAddress")} *</Label>
            <Input
              value={billing.address}
              onChange={(e) => onBillingChange({ ...billing, address: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.postalCode")} *</Label>
            <Input
              value={billing.postalCode}
              onChange={(e) => onBillingChange({ ...billing, postalCode: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.city")} *</Label>
            <Input
              value={billing.city}
              onChange={(e) => onBillingChange({ ...billing, city: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.country")} *</Label>
            <select
              value={billing.country}
              onChange={(e) => onBillingChange({ ...billing, country: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {COUNTRIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.reference")}</Label>
            <Input
              value={billing.reference}
              onChange={(e) => onBillingChange({ ...billing, reference: e.target.value })}
            />
            <p className="text-xs text-muted-foreground mt-1">{t("order.referenceHint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.poNumber")}</Label>
            <Input
              value={billing.poNumber}
              onChange={(e) => onBillingChange({ ...billing, poNumber: e.target.value })}
            />
            <p className="text-xs text-muted-foreground mt-1">{t("order.poNumberHint")}</p>
          </div>

          {/* Invoice method */}
          <div className="space-y-3 sm:col-span-2">
            <Label className="text-base font-semibold">{t("order.invoiceMethod")} *</Label>
            <RadioGroup
              value={billing.invoiceMethod}
              onValueChange={(v) =>
                onBillingChange({ ...billing, invoiceMethod: v as BillingInfo["invoiceMethod"] })
              }
              className="flex gap-6"
            >
              {(["email", "einvoice", "letter"] as const).map((method) => (
                <div key={method} className="flex items-center gap-2">
                  <RadioGroupItem value={method} id={`invoice-${method}`} />
                  <Label htmlFor={`invoice-${method}`} className="font-normal cursor-pointer">
                    {t(`order.invoiceMethods.${method}`)}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {billing.invoiceMethod === "email" && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t("order.invoiceEmail")} *</Label>
              <Input
                type="email"
                value={billing.invoiceEmail}
                onChange={(e) => onBillingChange({ ...billing, invoiceEmail: e.target.value })}
              />
            </div>
          )}

          {billing.invoiceMethod === "einvoice" && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t("order.peppolId")} *</Label>
              <Input
                value={billing.invoiceEmail}
                onChange={(e) => onBillingChange({ ...billing, invoiceEmail: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">{t("order.peppolIdHint")}</p>
            </div>
          )}

          {billing.invoiceMethod === "letter" && (
            <div className="sm:col-span-2 flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border">
              <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground">{t("order.letterNotice")}</p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>{t("order.vatNumber")}</Label>
            <Input
              value={billing.vatNumber}
              onChange={(e) => onBillingChange({ ...billing, vatNumber: e.target.value })}
            />
          </div>
        </div>
      </section>

      {/* Terms */}
      <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card">
        <Checkbox
          id="terms"
          checked={termsAccepted}
          onCheckedChange={(checked) => onTermsChange(checked === true)}
          className="mt-0.5"
        />
        <Label htmlFor="terms" className="text-sm font-normal leading-relaxed cursor-pointer">
          {t("order.termsPrefix")}{" "}
          <Link to={getLocalizedPath("/information/villkor")} target="_blank" className="text-primary underline hover:no-underline">
            {t("order.termsLink")}
          </Link>
        </Label>
      </div>
    </div>
  );
}
